import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Input,
  Slider,
  Progress,
  Alert,
  Form,
  notification,
} from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { uploadBenchmarkInputFolder } from "@/lib/api/resources/benchmark";
import { InferenceOptionsOverrideEditor } from "@/components/ai-connection/InferenceOptionsOverrideEditor";
import {
  executeSyntheticEmailBatch,
  SyntheticEmailScenario,
} from "@/lib/api/resources/agents";

import { buildEml, createEmlFile } from "@/lib/helpers/eml";

interface GenerateSyntheticEmailsModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: (folderName: string) => void;
}

const ALL_OPTIONAL_ATTRIBUTES = [
  "source",
  "urgent",
  "address",
  "damageAt",
  "towedAway",
  "driverType",
  "kilometers",
  "fleetRecord",
  "insurerName",
  "polisNumber",
  "damageNumber",
  "damagedParty",
  "leaseCompany",
  "matchedRules",
  "policeReport",
  "typeOfDamage",
  "causeOfDamage",
  "damageCountry",
  "guaranteeFund",
  "insuranceType",
  "paymentByType",
  "pickupService",
  "accidentReport",
  "damageReported",
  "personalInjury",
  "servicePartner",
  "urgencyComment",
  "damageClaimable",
  "hasPoliceReport",
  "repairRequested",
  "signatureClient",
  "insuranceCompany",
  "leaseCompanyName",
  "responsibleParty",
  "witnessStatement",
  "additionalComment",
  "commentFromDriver",
  "damageDescription",
  "dealershipCompany",
  "isDeviatingDriver",
  "otherPartyInvolved",
  "replacementVehicle",
  "serviceAdvisorName",
  "appointmentTimeSlot",
  "damageFromDirection",
  "photoCaptureMethods",
  "serviceAdvisorEmail",
  "replacementVehicleType",
  "documentTemplateComment",
  "vehicleHandInEndDateTime",
  "replacementVehicleComment",
  "vehicleHandInLocationName",
  "vehicleHandInStartDateTime",
  "vehicleHandInLocationAddress",
];

export const GenerateSyntheticEmailsModal = ({
  open,
  onCancel,
  onSuccess,
}: GenerateSyntheticEmailsModalProps) => {
  const [form] = Form.useForm();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [aiProfileId, setAiProfileId] = useState<string | null>(null);
  const [aiOverrides, setAiOverrides] = useState<Record<string, any> | null>(
    null,
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      const savedProfileId = localStorage.getItem(
        "rumbledirect.synthetic.profileId",
      );
      const savedOverridesStr = localStorage.getItem(
        "rumbledirect.synthetic.overrides",
      );

      if (savedProfileId) setAiProfileId(savedProfileId);
      if (savedOverridesStr) {
        try {
          setAiOverrides(JSON.parse(savedOverridesStr));
        } catch {}
      }

      const defaultFolder = `synthetic-damage-reports-${new Date().toISOString().split("T")[0]}`;
      form.setFieldsValue({ count: 20, folderName: defaultFolder });
      setProgress(0);
      setStatusMessage("");
      setErrorMessage(null);
      setIsGenerating(false);
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort("Component unmounted");
      }
    };
  }, [open]);

  const uploadMutation = useMutation({
    mutationFn: ({
      folderName,
      files,
    }: {
      folderName: string;
      files: File[];
    }) => uploadBenchmarkInputFolder("eml_folder", folderName, files),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["benchmark_input_folders"] });
      notification.success({
        title: "Folder generated and uploaded successfully.",
      });
      onSuccess(variables.folderName.trim().replace(/[^a-zA-Z0-9_-]/g, "-"));
      onCancel();
    },
    onError: (error: any) => {
      setErrorMessage(
        error?.response?.data?.detail || "Failed to upload generated folder.",
      );
      setIsGenerating(false);
    },
  });

  const generateBatch = async (
    profileId: string,
    options: Record<string, unknown> | null | undefined,
    signal: AbortSignal,
    batchCount: number,
    _targetCount: number,
    mandatoryBatchAttributes: string[],
  ): Promise<SyntheticEmailScenario[]> => {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        if (signal.aborted) throw new Error("AbortError");

        const response = await executeSyntheticEmailBatch({
          profileId,
          options: options ?? undefined,
          count: batchCount,
          attributes: mandatoryBatchAttributes,
        });

        if (!response.success) {
          throw new Error(`API Error: ${response.errorMessage}`);
        }

        if (!response.scenarios || response.scenarios.length === 0) {
          throw new Error("No scenarios in response");
        }

        return response.scenarios;
      } catch (e: any) {
        if (e.message === "AbortError" || e.name === "AbortError") throw e;
        if (attempt === 3) throw e;
        await new Promise((r) => setTimeout(r, 2000));
      }
    }
    return [];
  };

  const handleGenerate = async () => {
    try {
      const values = await form.validateFields();

      // The generation contract is profile-based: no connection string is assembled client-side. The
      // selected AI profile identifies the provider/model/credentials server-side; any inference-options
      // override from the InferenceOptionsOverrideEditor is sent alongside it.
      if (!aiProfileId) {
        setErrorMessage("Please select an AI profile.");
        return;
      }

      setErrorMessage(null);
      setIsGenerating(true);
      setProgress(0);
      setStatusMessage("Starting generation...");

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      localStorage.setItem(
        "rumbledirect.synthetic.profileId",
        aiProfileId || "",
      );
      localStorage.setItem(
        "rumbledirect.synthetic.overrides",
        JSON.stringify(aiOverrides || {}),
      );

      const targetCount = values.count;
      const batchSize = 10;
      const batches = Math.ceil(targetCount / batchSize);

      let allScenarios: SyntheticEmailScenario[] = [];
      let encounteredError = false;
      let lastErrorMsg = "";

      const shuffledAttributes = [...ALL_OPTIONAL_ATTRIBUTES].sort(
        () => 0.5 - Math.random(),
      );

      for (let i = 1; i <= batches; i++) {
        const remaining = targetCount - allScenarios.length;
        const currentBatchSize = Math.min(batchSize, remaining);

        const chunkStart = ((i - 1) * shuffledAttributes.length) / batches;
        const chunkEnd = (i * shuffledAttributes.length) / batches;
        const batchAttributes = shuffledAttributes.slice(
          Math.floor(chunkStart),
          Math.floor(chunkEnd),
        );

        setStatusMessage(`Generating batch ${i} of ${batches}...`);
        try {
          const scenarios = await generateBatch(
            aiProfileId,
            aiOverrides?.options,
            signal,
            currentBatchSize,
            targetCount,
            batchAttributes,
          );
          allScenarios = allScenarios.concat(scenarios);
          setProgress(Math.round((i / batches) * 80)); // Generation is 80% of the progress
        } catch (err: any) {
          if (err.name === "AbortError") throw err;
          encounteredError = true;
          lastErrorMsg = err.message || "Unknown error";
          break;
        }
      }

      if (allScenarios.length === 0) {
        throw new Error(
          encounteredError
            ? `Generation failed: ${lastErrorMsg}`
            : "No emails were generated.",
        );
      }

      if (encounteredError) {
        notification.warning({
          title: "Partial Success",
          description: `Generated ${allScenarios.length} out of ${targetCount} emails. The rest failed: ${lastErrorMsg}`,
          duration: 0,
        });
      }

      // Trim to exact requested count if we over-generated
      if (allScenarios.length > targetCount) {
        allScenarios = allScenarios.slice(0, targetCount);
      }

      setStatusMessage("Assembling EML files...");
      const files: File[] = [];
      allScenarios.forEach((scenario, index) => {
        const emlContent = buildEml(scenario);
        const filename = `synthetic_damage_${index + 1}.eml`;
        const file = createEmlFile(emlContent, values.folderName, filename);
        files.push(file);
      });

      setProgress(90);
      setStatusMessage("Uploading to server...");

      uploadMutation.mutate({ folderName: values.folderName, files });
    } catch (e: any) {
      if (e.name === "AbortError") return; // Exit quietly without React state updates
      if (e.name !== "ValidationError") {
        setErrorMessage(`Generation failed: ${e.message}`);
        setIsGenerating(false);
      }
    }
  };

  const handleCancel = () => {
    if (isGenerating && abortControllerRef.current) {
      abortControllerRef.current.abort("User cancelled generation");
    }
    onCancel();
  };

  return (
    <Modal
      title="Generate Synthetic Emails"
      open={open}
      onCancel={handleCancel}
      onOk={handleGenerate}
      okText="Generate & Upload"
      confirmLoading={isGenerating}
      maskClosable={!isGenerating}
      zIndex={1050}
    >
      <Alert
        message="AI Data Generator"
        description="This tool uses an LLM to dynamically generate realistic Dutch damage report emails (with mock images and PDFs). Perfect for testing mail parsing pipelines without using real customer data."
        type="info"
        showIcon
        className="mb-4"
      />

      {isGenerating ? (
        <div className="py-8 flex flex-col items-center justify-center">
          <Progress type="circle" percent={progress} />
          <p className="mt-4 text-gray-600 font-medium">{statusMessage}</p>
        </div>
      ) : (
        <Form form={form} layout="vertical">
          {errorMessage && (
            <Alert
              message={errorMessage}
              type="error"
              showIcon
              className="mb-4"
              closable
              onClose={() => setErrorMessage(null)}
            />
          )}
          <div className="mb-4">
            <InferenceOptionsOverrideEditor
              value={aiOverrides}
              profileId={aiProfileId}
              onChangeProfile={setAiProfileId}
              onChangeOverride={setAiOverrides}
              label="Select AI Profile"
              help="Choose a pre-configured LLM profile. The API key and model come from the profile; you may optionally override inference options."
            />
          </div>

          <Form.Item
            name="folderName"
            label="Target Folder Name"
            rules={[
              { required: true, message: "Please specify a folder name" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="count" label="Number of Emails to Generate">
            <Slider
              min={10}
              max={100}
              step={10}
              marks={{ 10: "10", 50: "50", 100: "100" }}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
